import React, { useEffect, useState } from 'react';
import { Plus, Trash2, Pencil, Check, X, BookOpen } from 'lucide-react';
import { AdminAPI } from '../../api';
import { toast } from 'sonner';

const Editor = ({ initial, onSave, onCancel, busy }) => {
  const [question, setQuestion] = useState(initial?.question || '');
  const [answer, setAnswer] = useState(initial?.answer || '');
  return (
    <div className="space-y-2">
      <input value={question} onChange={(e) => setQuestion(e.target.value)} placeholder="Question a customer might ask" data-testid="faq-question-input"
        className="w-full px-3 py-2 border border-[#D5D7ED] rounded-md text-[14px] focus:outline-none focus:border-[#0A1551]" />
      <textarea value={answer} onChange={(e) => setAnswer(e.target.value)} rows={4} placeholder="The exact answer the assistant should give" data-testid="faq-answer-input"
        className="w-full px-3 py-2 border border-[#D5D7ED] rounded-md text-[14px] focus:outline-none focus:border-[#0A1551]" />
      <div className="flex items-center gap-2">
        <button onClick={() => onSave(question, answer)} disabled={!question.trim() || !answer.trim() || busy} data-testid="faq-save"
          className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md bg-[#0F766E] text-white text-[13px] font-semibold disabled:opacity-50">
          <Check className="w-3.5 h-3.5" /> Save answer
        </button>
        <button onClick={onCancel} data-testid="faq-cancel" className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md border border-[#D5D7ED] text-[13px] font-medium text-[#0A1551]">
          <X className="w-3.5 h-3.5" /> Cancel
        </button>
      </div>
    </div>
  );
};

const KnowledgePanel = () => {
  const [items, setItems] = useState(null);
  const [adding, setAdding] = useState(false);
  const [editId, setEditId] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => AdminAPI.faqs().then(setItems).catch((e) => { setItems([]); toast.error(e?.response?.data?.detail || 'Failed to load answers'); });
  useEffect(() => { load(); }, []);

  const wrap = async (fn, ok) => {
    setBusy(true);
    try {
      await fn();
      toast.success(ok);
      setAdding(false);
      setEditId(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not save');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="max-w-[760px]" data-testid="admin-knowledge">
      <p className="text-[13px] text-[#6F76A7] mb-4">
        The assistant answers customers using these approved answers first. Anything not covered here is handed to customer service.
      </p>

      {items === null ? <p className="text-[13px] text-[#6F76A7]">Loading…</p> : items.length === 0 && !adding ? (
        <div className="bg-white border border-[#E3E5F5] rounded-xl p-10 text-center" data-testid="knowledge-empty">
          <BookOpen className="w-8 h-8 mx-auto text-[#B0B4D0] mb-2" />
          <p className="text-[14px] text-[#6F76A7]">No approved answers yet — add your first one below.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {items.map((f) => (
            <div key={f.id} className="bg-white border border-[#E3E5F5] rounded-xl p-4" data-testid={`faq-row-${f.id}`}>
              {editId === f.id ? (
                <Editor initial={f} busy={busy} onCancel={() => setEditId(null)}
                  onSave={(q, a) => wrap(() => AdminAPI.updateFaq(f.id, q, a), 'Answer updated')} />
              ) : (
                <>
                  <div className="flex items-start gap-2">
                    <p className="text-[14px] font-semibold text-[#0A1551] flex-1">{f.question}</p>
                    <button onClick={() => setEditId(f.id)} data-testid={`faq-edit-${f.id}`} className="p-1.5 rounded hover:bg-[#F0F1FB] text-[#6F76A7]" aria-label="Edit answer"><Pencil className="w-4 h-4" /></button>
                    <button onClick={() => wrap(() => AdminAPI.deleteFaq(f.id), 'Answer removed')} data-testid={`faq-delete-${f.id}`} className="p-1.5 rounded hover:bg-[#FEF2F2] text-[#E5387B]" aria-label="Delete answer"><Trash2 className="w-4 h-4" /></button>
                  </div>
                  <p className="text-[13px] text-[#4A517D] mt-1 whitespace-pre-line">{f.answer}</p>
                  {f.updated_by && <p className="text-[11px] text-[#9599C0] mt-2">Last edited by {f.updated_by}</p>}
                </>
              )}
            </div>
          ))}
        </div>
      )}

      {adding ? (
        <div className="bg-white border border-[#E3E5F5] rounded-xl p-4 mt-3">
          <Editor busy={busy} onCancel={() => setAdding(false)} onSave={(q, a) => wrap(() => AdminAPI.createFaq(q, a), 'Answer added')} />
        </div>
      ) : (
        <button onClick={() => setAdding(true)} data-testid="faq-add"
          className="inline-flex items-center gap-1.5 mt-4 px-4 py-2.5 rounded-md bg-[#0A1551] text-white text-[13px] font-semibold hover:bg-[#141F6B]">
          <Plus className="w-4 h-4" /> Add an answer
        </button>
      )}
    </div>
  );
};

export default KnowledgePanel;
